import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Performance = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">⚡ CSS Performance</h1>

      <p className="tutorial-intro">
        Writing efficient CSS is not just about making websites look good—it’s
        also about ensuring that they load fast and render smoothly. Poor CSS
        can cause slow page rendering, layout shifts, and unnecessary CPU/GPU
        usage. Let’s learn some <b>performance best practices</b> with examples.
      </p>
      {/* ✅ Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          🔑 Key Points
        </h2>
        <ul
          style={{
            lineHeight: "1.8",
            fontSize: "1.05rem",
            paddingLeft: "25px",
          }}
        >
          <li>
            Keep selectors <b>short and efficient</b> (avoid overly nested
            selectors).
          </li>
          <li>
            Use <code>classes</code> instead of <code>IDs</code> for reusable
            styling.
          </li>
          <li>
            Minimize the use of <code>!important</code> (it increases CSS
            specificity complexity).
          </li>
          <li>Group similar styles together and remove unused CSS rules.</li>
          <li>
            Consider using <b>CSS variables</b> for consistency and easier
            updates.
          </li>
          <li>
            Use <b>responsive units</b> (<code>rem</code>, <code>em</code>,{" "}
            <code>%</code>) for better scalability.
          </li>
          <li>
            Minify and combine CSS files in production to reduce network
            requests.
          </li>
        </ul>
      </div>

      {/* Example 1: Efficient Selectors */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: Use Efficient Selectors</h2>
        <p>
          Browsers read selectors <b>right to left</b>. Overly complex selectors
          (like <code>div ul li a span</code>) slow down rendering. Prefer
          <b>classes</b> over nested or universal selectors.
        </p>

        <CodeCopyBlock
          code={`/* ❌ Bad (deep nested selectors) */
div ul li a span {
  color: red;
}

/* ✅ Good (direct class selector) */
.nav-link {
  color: red;
}`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <p>
            ✅ Use <code>.nav-link</code> instead of chaining multiple elements.
          </p>
        </div>
      </div>

      {/* Example 2: Minimize Repaints and Reflows */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 2: Minimize Reflows & Repaints
        </h2>
        <p>
          Some properties (like <code>top</code>, <code>left</code>,{" "}
          <code>width</code>, <code>height</code>) trigger expensive{" "}
          <b>reflows</b>. Use <code>transform</code> and <code>opacity</code>{" "}
          instead for smoother animations.
        </p>

        <CodeCopyBlock
          code={`/* ❌ Triggers reflow (slow) */
.box {
  position: relative;
  left: 100px;
}

/* ✅ Uses GPU for smoother performance */
.box {
  transform: translateX(100px);
}`}
          language="css"
        />

        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            gap: "1rem",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              background: "#fca5a5",
              position: "relative",
              left: "20px",
            }}
          >
            Reflow
          </div>
          <div
            style={{
              width: "80px",
              height: "80px",
              background: "#86efac",
              transform: "translateX(20px)",
            }}
          >
            Transform
          </div>
        </div>
      </div>

      {/* Example 3: Avoid Expensive CSS */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: Avoid Expensive CSS</h2>
        <p>
          Some CSS rules are <b>performance heavy</b>, like{" "}
          <code>box-shadow</code>, <code>border-radius</code> (on big images),
          and <code>:nth-child()</code> on large lists. Use them wisely.
        </p>

        <CodeCopyBlock
          code={`/* ❌ Heavy shadow on many elements */
.card {
  box-shadow: 10px 10px 30px black;
}

/* ✅ Lighter shadow */
.card {
  box-shadow: 2px 2px 6px rgba(0,0,0,0.2);
}`}
          language="css"
        />

        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            gap: "1rem",
          }}
        >
          <div
            style={{
              width: "100px",
              height: "60px",
              background: "#e0f2fe",
              boxShadow: "10px 10px 30px black",
            }}
          >
            Heavy Shadow
          </div>
          <div
            style={{
              width: "100px",
              height: "60px",
              background: "#bae6fd",
              boxShadow: "2px 2px 6px rgba(0,0,0,0.2)",
            }}
          >
            Light Shadow
          </div>
        </div>
      </div>

      {/* Example 4: Use CSS Shorthand */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 4: Use Shorthand Properties
        </h2>
        <p>
          Shorthand properties reduce CSS size and improve readability, which
          indirectly improves performance.
        </p>

        <CodeCopyBlock
          code={`/* ❌ Long-hand */
margin-top: 10px;
margin-right: 20px;
margin-bottom: 10px;
margin-left: 20px;

/* ✅ Shorthand */
margin: 10px 20px;`}
          language="css"
        />

        <p style={{ marginTop: "1rem" }}>
          ✅ Always prefer shorthand when possible.
        </p>
      </div>

      {/* Example 5: Minify & Combine CSS */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 5: Minify & Combine CSS</h2>
        <p>
          Reducing the number of <b>CSS files</b> and removing unnecessary
          whitespace/comments helps browsers load styles faster.
        </p>

        <CodeCopyBlock
          code={`/* Normal */
body {
  background: white;
  color: black;
}

/* Minified */
body{background:white;color:black;}`}
          language="css"
        />

        <p style={{ marginTop: "1rem" }}>
          ✅ Use tools like <b>PostCSS, CSSNano, or PurgeCSS</b> in production.
        </p>
      </div>

      {/* Best Practices */}
      <div className="tutorial-section">
        <h2 className="section-title">✅ Best Practices for CSS Performance</h2>
        <ul style={{ lineHeight: "1.8" }}>
          <li>Use classes instead of deep/nested selectors.</li>
          <li>
            Prefer <code>transform</code> & <code>opacity</code> for animations.
          </li>
          <li>
            Avoid <code>*</code> universal selectors in production.
          </li>
          <li>
            Minimize expensive styles like heavy <code>box-shadow</code>.
          </li>
          <li>Remove unused CSS with tools like PurgeCSS.</li>
          <li>
            Use modern CSS features (grid, flex) instead of hacky workarounds.
          </li>
          <li>Always minify and combine CSS files before deployment.</li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Start reviewing your CSS with browser DevTools (<b>Performance tab</b>{" "}
          & <b>Rendering tools</b>) to spot slow styles. Focus on selectors,
          animations, and unused CSS for the biggest gains.
        </p>
      </div>
    </div>
  );
};

export default CSS_Performance;
